import { inject, injectable } from "inversify";
import { ScenarioElement } from "../../../model/playerConfiguration/ScenarioElement";
import { ScenarioGlobalState } from "../../../model/scenarioGlobalState/ScenarioGlobalState";
import { TYPES } from "../../di/types";
import { PosterLayer } from "../../layer/PosterLayer";
import { VideoLayer } from "../../layer/VideoLayer";
import { ResourcesCache } from "../../loader/scenarioLoader/ResourcesCache";

export interface PosterLayerManager {
  newScenarioElement(scenarioGlobalState: ScenarioGlobalState, progress: number): void;
  resourcesLoaded(resourcesCache: ResourcesCache): void;
  scenarioElementEnded(): void;
  setCurrentVideoSnapshotAsPoster(): void;
  showPosterOrSnapshot(scenarioElement: ScenarioElement, progress: number): void;
  showPoster(posterUrl: string): void;
  hidePoster(): void;
  showInitialPoster(posterUrl: string, aspectRatio: number): void;
  getIsShowingPoster(): boolean;
}

@injectable()
export class PosterLayerManagerImpl implements PosterLayerManager {
  private scenarioElement: ScenarioElement;
  private resourcesCache: ResourcesCache;

  constructor(
    @inject(TYPES.layer.PosterLayer) private posterLayer: PosterLayer,
    @inject(TYPES.layer.VideoLayer) private videoLayer: VideoLayer,
  ) {}

  newScenarioElement(scenarioGlobalState: ScenarioGlobalState, progress: number): void {
    this.scenarioElement = scenarioGlobalState.scenarioPath.currentElement;

    if (this.scenarioElement.poster && progress === 0) {
      this.showPoster(this.scenarioElement.poster);
    }
  }

  resourcesLoaded(resourcesCache: ResourcesCache): void {
    this.resourcesCache = resourcesCache;

    if (this.scenarioElement.isCTAOnlyElement && this.scenarioElement.poster) {
      this.showPoster(this.scenarioElement.poster);
    }
  }

  scenarioElementEnded(): void {
    if (this.scenarioElement.video) {
      const triggerTypeEndCTA = this.scenarioElement.ctas.find((cta) => cta.triggerType === "END");
      if (!triggerTypeEndCTA || !triggerTypeEndCTA.poster) {
        this.setCurrentVideoSnapshotAsPoster();
      }
    }
  }

  showPosterOrSnapshot(scenarioElement: ScenarioElement, progress: number) {
    if (scenarioElement.poster && progress === 0) {
      this.showPoster(scenarioElement.poster);
    } else {
      this.setCurrentVideoSnapshotAsPoster();
    }
  }

  setCurrentVideoSnapshotAsPoster(): void {
    if (this.scenarioElement.video) {
      const videoSnapshot = this.videoLayer.captureSnapshot();
      if (videoSnapshot) {
        this.showPoster(videoSnapshot);
      }
    }
  }

  hidePoster(): void {
    this.posterLayer.hidePoster();
  }

  showInitialPoster(posterUrl: string, aspectRatio: number) {
    this.posterLayer.setAspectRatio(aspectRatio);
    // this poster isn't cached
    this.showPoster(posterUrl);
  }

  getIsShowingPoster(): boolean {
    return this.posterLayer.getIsShowingPoster();
  }

  showPoster(posterUrl: string): void {
    this.posterLayer.showPoster(posterUrl);
  }
}
